import type { OnboardingAuditLog } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type ActivityEntry = OnboardingAuditLog & { actorName?: string | null };

interface Props {
  entries: ActivityEntry[];
}

const ACTION_LABELS: Record<string, string> = {
  "onboarding.started": "Onboarding iniciado",
  "onboarding.started_from_draft": "Onboarding iniciado (rascunho)",
  "onboarding.canceled": "Onboarding cancelado",
  "onboarding.graduated": "Onboarding finalizado",
  "onboarding.auto_graduated": "Onboarding finalizado automaticamente",
  "onboarding.buddy_changed": "Buddy alterado",
  "onboarding.draft_created": "Rascunho criado",
  "onboarding.draft_discarded": "Rascunho descartado",
  "onboarding.todo_added": "To-do adicionado",
  "onboarding.todo_deleted": "To-do removido",
  "onboarding.todo_done": "To-do concluído",
  "onboarding.todo_updated": "To-do atualizado",
  "onboarding.todo_marked_done": "To-do marcado como concluído",
  "onboarding.todo_marked_pending": "To-do marcado como pendente",

  "snapshot_assignment.added": "Recurso adicionado à jornada",
  "snapshot_assignment.removed": "Recurso removido da jornada",
  "snapshot_assignment.moved": "Recurso reposicionado na jornada",
  "snapshot_assignment.scope_changed": "Escopo do recurso alterado",

  "port.resource.redirect_clicked": "Recurso acessado (portal)",
  "port.todo.checked": "To-do concluído (portal)",
  "port.todo.unchecked": "To-do reaberto (portal)",
  "port.tool.configured": "Ferramenta configurada (portal)",
  "port.milestone.item.checked": "Item do milestone concluído (portal)",
  "port.milestone.item.unchecked": "Item do milestone reaberto (portal)",
  "port.milestone.completed_100": "Milestone 100% concluído",
  "port.survey.submitted": "Pesquisa respondida (portal)",
  "port.notion.fetch_failed": "Falha ao buscar conteúdo Notion",
  "port.tool.ask_lookup_failed": "Falha ao buscar dono da ferramenta",

  "resource.created": "Recurso criado",
  "resource.updated": "Recurso atualizado",
  "resource.moved": "Recurso movido",
  "resource.deleted": "Recurso excluído",
  "resource.archived": "Recurso arquivado",
  "resource.restored": "Recurso restaurado",
  "resource.source_attached": "Fonte vinculada ao recurso",
  "resource.content_set": "Conteúdo do recurso definido",
  "resource.render_type_set": "Tipo de renderização alterado",
  "resource.progress_trackable_set": "Rastreamento de progresso alterado",
  "resource.resync_initiated": "Re-sincronização iniciada",
  "resource.deadline_updated": "Prazo do recurso atualizado",
  "resource.disciplines_updated": "Disciplinas do recurso atualizadas",
  "resource.category_updated": "Categoria do recurso atualizada",
};

function formatTimestamp(d: Date | string): string {
  const date = d instanceof Date ? d : new Date(d);
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function ActivityFeed({ entries }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Atividade</CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma atividade registrada ainda.</p>
        ) : (
          <ul className="space-y-3">
            {entries.map((e) => (
              <li
                key={e.id}
                className="flex items-start justify-between gap-3 border-b pb-2 last:border-b-0 last:pb-0"
                data-testid={`activity-entry-${e.id}`}
              >
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium break-words">{ACTION_LABELS[e.action] ?? e.action}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    por {e.actorName ?? "Usuário do sistema"}
                  </p>
                </div>
                <p className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                  {formatTimestamp(e.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
